import { useState } from "react";
import {
  type AuthenticationMethod,
  deleteAuthenticationMethod,
  fetchAuthenticationMethods,
  isApiError,
  requestPasswordChange,
  type SessionUser,
  STEP_UP_URL,
  startPasskeyEnrollment,
  stepUpUrl,
  verifyPasskeyEnrollment,
} from "../api.ts";
import { ConfirmDialog } from "../components/Dialog.tsx";
import {
  CheckIcon,
  KeyIcon,
  LaptopIcon,
  LockIcon,
  MessageIcon,
  PhoneIcon,
  PlusIcon,
  ShieldIcon,
} from "../components/icons.tsx";
import {
  Card,
  LoadState,
  Note,
  PageHeader,
  SectionIcon,
  type Status,
  StatusMessage,
} from "../components/ui.tsx";
import { errorMessage } from "../errors.ts";
import { formatDate, summarizeUserAgent } from "../format.ts";
import { ROUTES } from "../router.ts";
import { redirect, useLoader } from "../use-loader.ts";
import { createPasskey, isPasskeySupported } from "../webauthn.ts";
import { type MfaKind, SmsEnrollDialog, TotpEnrollDialog } from "./MfaEnrollDialogs.tsx";

const MFA_LABELS: Record<MfaKind, string> = { totp: "認証アプリ", phone: "SMS" };

export function SecurityPage({ user }: { user: SessionUser }) {
  const methods = useLoader<AuthenticationMethod[]>(fetchAuthenticationMethods);
  const [stepUpRequired, setStepUpRequired] = useState(false);

  /** 変更系で step-up が要ると分かったら、一覧の代わりに本人確認の案内を出す */
  const onError = (error: unknown) => {
    if (isApiError(error, "STEP_UP_REQUIRED")) {
      setStepUpRequired(true);
      return true;
    }
    return false;
  };

  const needsStepUp =
    stepUpRequired ||
    (methods.state.status === "error" && isApiError(methods.state.error, "STEP_UP_REQUIRED"));

  return (
    <>
      <PageHeader
        title="ログインとセキュリティ"
        description="ログインに使うパスワードと、本人確認のための認証方法を管理します。"
      />
      <PasswordSection user={user} />
      {needsStepUp ? (
        <StepUpSection />
      ) : methods.state.status === "loaded" ? (
        <>
          <MfaSection methods={methods.state.data} onChanged={methods.reload} onError={onError} />
          <PasskeySection
            methods={methods.state.data}
            onChanged={methods.reload}
            onError={onError}
          />
        </>
      ) : (
        <Card label="多要素認証とパスキー">
          <LoadState state={methods.state.status} onRetry={methods.reload} />
        </Card>
      )}
    </>
  );
}

function PasswordSection({ user }: { user: SessionUser }) {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<Status>(null);
  const lastChanged = formatDate(user.lastPasswordReset);
  const email = user.email ?? "ご登録のメールアドレス";

  const onClick = async () => {
    setBusy(true);
    setStatus(null);
    try {
      await requestPasswordChange();
      setStatus({
        tone: "success",
        text: `${email} 宛にパスワード変更用のリンクを送信しました。メールをご確認ください。`,
      });
    } catch (error) {
      setStatus({
        tone: "error",
        text: isApiError(error, "TOO_MANY_REQUESTS")
          ? `変更用のメールは送信済みです。届かない場合は、${
              error.retryAfterSeconds !== null && error.retryAfterSeconds >= 60
                ? `${Math.ceil(error.retryAfterSeconds / 60)} 分ほど`
                : "しばらく"
            }待ってから再度お試しください。`
          : errorMessage(error, "メールを送信できませんでした。"),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card labelledBy="pw-heading">
      <div className="section-head section-head--center">
        <div className="section-head__title">
          <SectionIcon>
            <LockIcon size={22} />
          </SectionIcon>
          <div>
            <h2 id="pw-heading">パスワード</h2>
            <p>最終変更日: {lastChanged ?? "記録なし"}</p>
          </div>
        </div>
        <button
          type="button"
          className="button button--secondary button--bold"
          onClick={onClick}
          disabled={busy}
        >
          {busy ? "送信中…" : "パスワードを変更"}
        </button>
      </div>
      <p className="inset-text">
        変更用のリンクを {email}{" "}
        宛にお送りします。メールのリンクから新しいパスワードを設定してください。
      </p>
      <StatusMessage status={status} />
    </Card>
  );
}

function StepUpSection() {
  return (
    <Card labelledBy="stepup-heading">
      <div className="section-head__title">
        <SectionIcon>
          <ShieldIcon size={22} />
        </SectionIcon>
        <div>
          <h2 id="stepup-heading">多要素認証とパスキー</h2>
          <p>設定を確認・変更するには、本人確認が必要です。</p>
        </div>
      </div>
      <p className="inset-text">
        安全のため、再ログインしてください。ログイン後 15 分間、設定を変更できます。
      </p>
      <div className="section-actions">
        <button
          type="button"
          className="button button--primary"
          onClick={() => redirect.to(stepUpUrl(STEP_UP_URL, ROUTES.security))}
        >
          再ログインする
        </button>
      </div>
    </Card>
  );
}

type SectionProps = {
  methods: AuthenticationMethod[];
  onChanged: () => void;
  /** step-up が要るエラーなら true を返す（呼び出し元は自分の表示を閉じる） */
  onError: (error: unknown) => boolean;
};

type Pending =
  | { action: "enroll"; kind: MfaKind }
  | { action: "delete"; kind: MfaKind; method: AuthenticationMethod }
  | null;

function MfaSection({ methods, onChanged, onError }: SectionProps) {
  const mfa = methods.filter(
    (m): m is AuthenticationMethod & { type: MfaKind } =>
      (m.type === "totp" || m.type === "phone") && m.confirmed !== false
  );
  const totp = mfa.find((m) => m.type === "totp") ?? null;
  const phone = mfa.find((m) => m.type === "phone") ?? null;
  const enabled = mfa.length > 0;
  const [pending, setPending] = useState<Pending>(null);
  const [busy, setBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>(null);

  const onDelete = async () => {
    if (pending?.action !== "delete") return;
    setBusy(true);
    setDeleteError(null);
    try {
      await deleteAuthenticationMethod(pending.method.id);
      setStatus({ tone: "success", text: `${MFA_LABELS[pending.kind]}を削除しました。` });
      setPending(null);
      onChanged();
    } catch (error) {
      if (onError(error)) return;
      setDeleteError(errorMessage(error, "削除できませんでした。"));
    } finally {
      setBusy(false);
    }
  };

  const enroll = (kind: MfaKind) => {
    setStatus(null);
    setPending({ action: "enroll", kind });
  };
  const remove = (kind: MfaKind, method: AuthenticationMethod) => {
    setStatus(null);
    setDeleteError(null);
    setPending({ action: "delete", kind, method });
  };

  return (
    <Card labelledBy="mfa-heading">
      <div className="section-head">
        <div className="section-head__title">
          <SectionIcon>
            <ShieldIcon size={22} />
          </SectionIcon>
          <div>
            <h2 id="mfa-heading">多要素認証（MFA）</h2>
            <p>パスワードに加えて、登録した端末で本人確認を行います。</p>
          </div>
        </div>
        <span className={`pill${enabled ? "" : " pill--off"}`}>
          {enabled ? <CheckIcon size={14} /> : null}
          {enabled ? "有効" : "未設定"}
        </span>
      </div>

      <ul className="method-list">
        <MfaRow
          kind="totp"
          method={totp}
          icon={<PhoneIcon size={22} />}
          title="認証アプリ（ワンタイムパスワード）"
          detail={(m) => `Google Authenticator など · 登録日 ${formatDate(m.created_at) ?? "—"}`}
          unusedDetail="Google Authenticator などのアプリに表示される確認コードで本人確認します"
          otherEnabled={phone !== null}
          onEnroll={() => enroll("totp")}
          onDelete={(m) => remove("totp", m)}
        />
        <MfaRow
          kind="phone"
          method={phone}
          icon={<MessageIcon size={22} />}
          title="SMS"
          detail={(m) =>
            [m.phone_number, `登録日 ${formatDate(m.created_at) ?? "—"}`]
              .filter(Boolean)
              .join(" · ")
          }
          unusedDetail="携帯電話に届く確認コードで本人確認します"
          otherEnabled={totp !== null}
          onEnroll={() => enroll("phone")}
          onDelete={(m) => remove("phone", m)}
        />
      </ul>
      <StatusMessage status={status} />
      <Note>
        認証アプリかSMSのどちらか1つを登録できます。切り替えると、今の方法は解除されます。
      </Note>

      {pending?.action === "enroll" ? (
        <EnrollDialog
          kind={pending.kind}
          replaceIds={mfa.map((m) => m.id)}
          onClose={() => setPending(null)}
          onDone={(message) => {
            setPending(null);
            setStatus(message);
            onChanged();
          }}
          onError={(error) => {
            if (!onError(error)) return false;
            setPending(null);
            return true;
          }}
        />
      ) : null}
      {pending?.action === "delete" ? (
        <ConfirmDialog
          title={`多要素認証（${MFA_LABELS[pending.kind]}）を削除しますか？`}
          confirmLabel="削除する"
          onConfirm={onDelete}
          onCancel={() => setPending(null)}
          busy={busy}
          error={deleteError}
        >
          <p className="dialog__text">
            削除すると、ログイン時にこの方法で本人確認できなくなります。所属組織で多要素認証が必須になっている場合は、次回ログイン時にあらためて登録を求められます。
          </p>
        </ConfirmDialog>
      ) : null}
    </Card>
  );
}

function EnrollDialog(props: {
  kind: MfaKind;
  replaceIds: string[];
  onClose: () => void;
  onDone: (status: Status) => void;
  onError: (error: unknown) => boolean;
}) {
  const Component = props.kind === "totp" ? TotpEnrollDialog : SmsEnrollDialog;
  return <Component {...props} />;
}

function MfaRow(props: {
  kind: MfaKind;
  method: AuthenticationMethod | null;
  icon: React.ReactNode;
  title: string;
  detail: (m: AuthenticationMethod) => string;
  unusedDetail: string;
  otherEnabled: boolean;
  onEnroll: () => void;
  onDelete: (m: AuthenticationMethod) => void;
}) {
  const { method } = props;
  return (
    <li className={`method${method === null ? " method--unused" : ""}`}>
      <div className="method__info">
        <span className="method__icon">{props.icon}</span>
        <div>
          <div className="method__title">
            {props.title}
            {method === null ? <span className="tag">未使用</span> : null}
          </div>
          <div className="method__detail">
            {method === null ? props.unusedDetail : props.detail(method)}
          </div>
        </div>
      </div>
      {method === null ? (
        <button
          type="button"
          className="button button--secondary button--small"
          onClick={props.onEnroll}
        >
          {props.otherEnabled ? `${MFA_LABELS[props.kind]}に切り替える` : "設定する"}
        </button>
      ) : (
        <div className="button-row">
          <button
            type="button"
            className="button button--secondary button--small"
            onClick={props.onEnroll}
            aria-label={`${MFA_LABELS[props.kind]}を再設定`}
          >
            再設定
          </button>
          <button
            type="button"
            className="button button--danger button--small"
            onClick={() => props.onDelete(method)}
            aria-label={`${MFA_LABELS[props.kind]}を削除`}
          >
            削除
          </button>
        </div>
      )}
    </li>
  );
}

function passkeyTitle(method: AuthenticationMethod) {
  const ua = summarizeUserAgent(method.user_agent);
  return { title: method.name ?? ua.label ?? "パスキー", mobile: ua.mobile };
}

function PasskeySection({ methods, onChanged, onError }: SectionProps) {
  const passkeys = methods.filter((m) => m.type === "passkey");
  const [adding, setAdding] = useState(false);
  const [status, setStatus] = useState<Status>(null);
  const [target, setTarget] = useState<AuthenticationMethod | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const onAdd = async () => {
    setStatus(null);
    if (!isPasskeySupported()) {
      setStatus({ tone: "error", text: "このブラウザはパスキーに対応していません。" });
      return;
    }
    setAdding(true);
    try {
      const enrollment = await startPasskeyEnrollment();
      const credential = await createPasskey(enrollment.authn_params_public_key);
      // 登録前のパスキーの ID は passkey|new（My Account API の Location ヘッダーと同じ）
      await verifyPasskeyEnrollment(
        enrollment.id ?? "passkey|new",
        enrollment.auth_session,
        credential
      );
      setStatus({ tone: "success", text: "パスキーを追加しました。" });
      onChanged();
    } catch (error) {
      if (onError(error)) return;
      setStatus({ tone: "error", text: passkeyErrorMessage(error) });
    } finally {
      setAdding(false);
    }
  };

  const onDelete = async () => {
    if (target === null) return;
    setBusy(true);
    setDeleteError(null);
    try {
      await deleteAuthenticationMethod(target.id);
      setStatus({
        tone: "success",
        text: `パスキー「${passkeyTitle(target).title}」を削除しました。`,
      });
      setTarget(null);
      onChanged();
    } catch (error) {
      if (onError(error)) return;
      setDeleteError(errorMessage(error, "削除できませんでした。"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card labelledBy="pk-heading">
      <div className="section-head">
        <div className="section-head__title">
          <SectionIcon>
            <KeyIcon size={22} />
          </SectionIcon>
          <div>
            <h2 id="pk-heading">パスキー</h2>
            <p>指紋・顔認証や画面ロックで、パスワードを入力せずにログインできます。</p>
          </div>
        </div>
        <button type="button" className="button button--primary" onClick={onAdd} disabled={adding}>
          <PlusIcon size={16} />
          {adding ? "登録しています…" : "パスキーを追加"}
        </button>
      </div>

      {passkeys.length === 0 ? (
        <p className="empty empty--inset">登録しているパスキーはありません。</p>
      ) : (
        <ul className="method-list">
          {passkeys.map((m) => {
            const { title, mobile } = passkeyTitle(m);
            const detail = [
              m.credential_device_type === "multi_device" ? "同期されるパスキー" : null,
              `登録日 ${formatDate(m.created_at) ?? "—"}`,
              m.last_auth_at ? `最終使用 ${formatDate(m.last_auth_at)}` : null,
            ]
              .filter(Boolean)
              .join(" · ");
            return (
              <li key={m.id} className="method">
                <div className="method__info">
                  <span className="method__icon">
                    {mobile ? <PhoneIcon size={22} /> : <LaptopIcon size={22} />}
                  </span>
                  <div>
                    <div className="method__title">{title}</div>
                    <div className="method__detail">{detail}</div>
                  </div>
                </div>
                <button
                  type="button"
                  className="button button--danger button--small"
                  aria-label={`パスキー「${title}」を削除`}
                  onClick={() => {
                    setStatus(null);
                    setDeleteError(null);
                    setTarget(m);
                  }}
                >
                  削除
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <StatusMessage status={status} />
      <Note>
        パソコンとスマートフォンの両方に登録しておくと、どちらかの端末をなくしても自分でログインできます。
      </Note>

      {target !== null ? (
        <ConfirmDialog
          title={`パスキー「${passkeyTitle(target).title}」を削除しますか？`}
          confirmLabel="削除する"
          onConfirm={onDelete}
          onCancel={() => setTarget(null)}
          busy={busy}
          error={deleteError}
        >
          <p className="dialog__text">
            削除すると、この端末のパスキーではログインできなくなります。端末側に保存されたパスキーは、端末の設定から別途削除してください。
          </p>
          {passkeys.length === 1 ? (
            <Note tone="warning">
              最後のパスキーを削除する場合は、パスワードと多要素認証でログインすることになります。
            </Note>
          ) : null}
        </ConfirmDialog>
      ) : null}
    </Card>
  );
}

function passkeyErrorMessage(error: unknown): string {
  if (error instanceof DOMException) {
    if (error.name === "NotAllowedError" || error.name === "AbortError") {
      return "パスキーの登録を中止しました。";
    }
    if (error.name === "InvalidStateError") return "この端末のパスキーはすでに登録されています。";
    return "パスキーを登録できませんでした。";
  }
  return errorMessage(error, "パスキーを登録できませんでした。");
}
